import { AnyToken } from 'src/common/types/models/Tokens'
import clsx from 'clsx'
import { useState } from 'react'
import Modal, { ModalProps } from '../../../../ui-kit/Modal'
import TokenSelectorHeader from '../shared/TokenSelectorHeader'
import { useTokenSelectorTokens } from '../../hooks/useTokenSelectorTokens'
import '../../styles.css'
import TokenSelectorList from '../lists/TokenSelectorList'
import TokenSelectorSkeletonList from '../lists/TokenSelectorSkeletonList'
import { useTranslation } from 'react-i18next'
import { groups } from '../consts'
import TokenSelectorNoTokens from '../shared/TokenSelectorNoTokens'
import TokenInfo from 'src/common/components/ TokenInfo/ TokenInfo'
import { uniqBy } from 'lodash'
import { aggregateNFTCollection } from 'src/common/utils/collections/aggregateNFTBalances'
import useToggle from 'src/common/hooks/helpers/useToggle'

export interface TokenSelectorModalProps extends Omit<ModalProps, 'children'> {
  excludeToken?: AnyToken
  excludeNFT?: boolean
  onTokenSelect: (value: AnyToken) => void
  nftGroup?: boolean
  onlyOwned?: boolean
}

export type TokenSelectorGroup = (typeof groups)[number]

const TokenSelectorModal = ({
  open,
  onClose,
  excludeToken,
  excludeNFT,
  onTokenSelect,
  className,
  nftGroup,
  onlyOwned,
}: TokenSelectorModalProps) => {
  const [infoToken, setInfoToken] = useState<AnyToken | null>(null)

  const {
    isOn: isInfoModalOpen,
    on: openInfoModal,
    off: closeInfoModal,
  } = useToggle()

  const { t } = useTranslation('tokenSelector', { keyPrefix: 'tokenList' })
  const [group, setGroup] = useState<TokenSelectorGroup>('All')
  const {
    ownedTokens,
    notOwnedTokens,
    loading,
    debouncedSearch,
    setSearch: handleSearchChange,
  } = useTokenSelectorTokens(group, excludeToken, excludeNFT)

  const closeAndReset = () => {
    setGroup('All')
    handleSearchChange('')
    onClose?.()
  }

  const handleCloseModal = closeAndReset

  const handleSelectToken = (newToken: AnyToken) => {
    onTokenSelect(newToken)
    closeAndReset()
  }

  const handleTokenInfo = (token: AnyToken) => {
    setInfoToken(token)
    openInfoModal()
  }

  const _ownedTokens = nftGroup
    ? aggregateNFTCollection(ownedTokens)
    : ownedTokens
  const _notOwnedTokens = uniqBy(notOwnedTokens, 'contractAddress')
  const allTokens = onlyOwned
    ? [...ownedTokens]
    : [...ownedTokens, ...notOwnedTokens]

  return (
    <>
      <Modal
        className={clsx('pb-1 px-3 sm:max-w-md', className)}
        open={open}
        onClose={handleCloseModal}
      >
        <TokenInfo
          isOpen={isInfoModalOpen}
          token={infoToken}
          onClose={closeInfoModal}
        />
        <div className='flex flex-col gap-3' style={{ height: '80vh' }}>
          <TokenSelectorHeader />
          <div className='overflow-auto flex flex-col gap-3 h-full'>
            {loading ? (
              <TokenSelectorSkeletonList />
            ) : (
              <>
                {debouncedSearch ? (
                  <TokenSelectorList
                    tokens={allTokens}
                    title={t('titles.search')}
                    onSelectToken={handleSelectToken}
                    onInfoClick={handleTokenInfo}
                    nftGroup={nftGroup}
                  />
                ) : (
                  <>
                    <TokenSelectorList
                      tokens={_ownedTokens}
                      onSelectToken={handleSelectToken}
                      onInfoClick={handleTokenInfo}
                      nftGroup={nftGroup}
                    />
                    {!onlyOwned && (
                      <>
                        <hr />
                        <TokenSelectorList
                          tokens={_notOwnedTokens}
                          onSelectToken={handleSelectToken}
                          onInfoClick={handleTokenInfo}
                          nftGroup
                        />
                      </>
                    )}
                  </>
                )}
                {!allTokens.length && <TokenSelectorNoTokens />}
              </>
            )}
          </div>
        </div>
      </Modal>
    </>
  )
}

export default TokenSelectorModal
